import { NextPage } from 'next'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo } from '@/layout/Seo'
import { useMetaForProject } from '@/layout/Seo/ProjectSeoHelper'
import { Downloadable, getDownloadablesForProject } from '@/services/DownloadablesService'
import { getProjectMeta, ProjectMeta } from '@/services/ProjectsService'
import { useUser } from '@/services/UserService'
import { logger } from '@/utils/logging'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { isDefined } from '@/utils/primitives/generics'
import { loadTranslations } from '@/utils/translate/translate-server'
import { WatchProjectDownloadsView, WatchProjectDownloadsViewProps } from '@/views/WatchProjectDownloadsView'

interface WatchProjectDownloadsPageProps extends Omit<WatchProjectDownloadsViewProps, 'isLoggedIn'> {
  projectMeta: ProjectMeta
  locale: string
  downloadables: Downloadable[]
}

export const WatchProjectDownloadsPage: NextPage<WatchProjectDownloadsPageProps> = ({
  projectMeta,
  downloadables,
  locale,
}) => {
  const meta = useMetaForProject({ ...projectMeta, locale })
  const { isLoggedIn } = useUser()

  const title = `${projectMeta.name} Downloads | Fun Family Activities`
  const description = `Download coloring pages, activities, and recipes for ${projectMeta.name}. Perfect for family fun time and homeschool activities!`

  return (
    <ProjectMetaProvider {...projectMeta}>
      <Seo {...meta} title={title} description={description} />
      <WatchProjectDownloadsView downloadables={downloadables} projectMeta={projectMeta} isLoggedIn={isLoggedIn} />
    </ProjectMetaProvider>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<WatchProjectDownloadsPageProps>(
  async ({ federationClient, locale, resolvedUrl, params, next404, preview }) => {
    try {
      const translations = await loadTranslations(locale, ['common', 'watch'])
      const projectSlug = params?.project as string

      if (!projectSlug) {
        logger().error('Project slug is required', { resolvedUrl })
        return next404()
      }

      const projectMeta = await getProjectMeta({ slug: projectSlug }, federationClient, { locale })

      if (!projectMeta) {
        logger().warn('Project not found', { projectSlug, resolvedUrl })
        return next404()
      }

      const allDownloadables = await getDownloadablesForProject(projectSlug, { locale, preview })

      const projectDownloadables = allDownloadables?.filter(isDefined) ?? []

      return {
        props: {
          projectMeta,
          downloadables: projectDownloadables,
          locale,
          ...translations,
        },
      }
    } catch (error) {
      logger().error(`Error fetching downloads page data for ${resolvedUrl}`, { error })
      throw error
    }
  },
)

export default WatchProjectDownloadsPage
